import { z } from 'zod';
import type { FormField } from '../types';
import { isFormField, validateFormFields } from './form-capabilities';
import { evaluateQuestionCondition, isQuestionConditionDraft, questionConditionIssue, type ConditionAnswer, type QuestionCondition, CONDITION_OPERATORS, type QuestionConditionSource } from './question-conditions';

/** One source snapshot and its copied parent question identities. */
export interface FormBlock {
  id: string;
  title: string;
  sourceId: string;
  sourceFields: FormField[];
  /** Original grouping metadata retained for the separate source response. */
  sourceSections?: ConversationSection[];
  showWhen?: QuestionCondition;
  fieldIds: string[];
  /** Source section gates remapped to parent-owned question identities. */
  sections: ConversationSection[];
}

/** Authored organisation metadata, never a respondent step or answer key. */
export interface ConversationSection {
  id: string;
  title: string;
  description?: string;
  /** Every member also requires this rule to match. */
  showWhen?: QuestionCondition;
  fieldIds: string[];
  blocks?: FormBlock[];
  /** Empty headings sit immediately before this field; absent means the end. */
  beforeFieldId?: string;
}

/** A standalone embed uses an invisible owner so its questions can use the same routing contract. */
export function standaloneFormBlock(section: ConversationSection): boolean {
  const block = section.blocks?.[0];
  return Boolean(block && section.id === `nested_${block.id}` && section.blocks?.length === 1
    && !section.showWhen && !section.description && section.fieldIds.length === block.fieldIds.length);
}

const identity = z.string().trim().min(1).max(200).regex(/^[^<>\x00-\x1f\x7f]+$/);
const conditionSchema = z.object({ version: z.literal(1), match: z.enum(['all', 'any']),
  rules: z.array(z.object({ fieldId: z.string().min(1).max(200), operator: z.enum(CONDITION_OPERATORS),
    value: z.union([z.string().max(2000), z.number(), z.boolean()]).optional(),
    valueTo: z.union([z.string().max(2000), z.number()]).optional(), part: z.string().min(1).max(200).optional(),
  }).strict()).min(1).max(20),
}).strict().refine(isQuestionConditionDraft);
const fieldIdsSchema = z.array(z.string().min(1).max(200)).max(200).refine(ids => new Set(ids).size === ids.length);
const childSectionSchema = z.object({
  id: identity,
  title: z.string().trim().min(1).max(200),
  description: z.string().max(4000).optional(),
  showWhen: conditionSchema.optional(),
  fieldIds: fieldIdsSchema,
  beforeFieldId: z.string().min(1).max(200).optional(),
}).strict();

/** These captures require resources or consent scoped to their own response. */
export function isNestableField(value: unknown): value is FormField {
  return isFormField(value) && value.type !== 'file_upload' && value.type !== 'calendar_booking' && value.type !== 'signature';
}

const flatSectionSchema = childSectionSchema.extend({ blocks: z.array(z.never()).max(0).optional() });
const formBlockSchema = z.object({
  id: identity, title: z.string().trim().min(1).max(200), sourceId: z.uuid(),
  sourceFields: z.array(z.unknown().refine(isNestableField)).min(1).max(200),
  sourceSections: z.array(flatSectionSchema).max(200).optional(),
  showWhen: conditionSchema.optional(), fieldIds: fieldIdsSchema.min(1), sections: z.array(flatSectionSchema).max(200),
}).strict();

/** A bounded, strict shape shared by APIs, generation and stored configuration. */
export const conversationSectionSchema = childSectionSchema.extend({
  blocks: z.array(formBlockSchema).max(200).optional(),
});

/** Metadata issues stay separate from question validation and question limits. */
export interface ConversationSectionIssue { sectionId: string; index: number; reason: string; message: string }

/** Renderable contiguous blocks preserve the canonical question sequence. */
export type ConversationSectionBlock =
  | { kind: 'section'; section: ConversationSection; fields: FormField[]; startIndex: number }
  | { kind: 'ungrouped'; fields: FormField[]; startIndex: number };

/** Validate membership against the authoritative field order at write boundaries. */
export function validateConversationSections(fields: readonly FormField[], sections?: readonly ConversationSection[]): ConversationSectionIssue[] {
  if (!sections) return [];
  const issues: ConversationSectionIssue[] = [];
  const positions = new Map(fields.map((field, index) => [field.id, index]));
  const owners = new Map<string, string>();
  const nestedIds = new Set<string>();
  const ids = new Set<string>();
  const ranges: { start: number; end: number }[] = [];
  let previousEnd = 0;
  const add = (section: ConversationSection, index: number, reason: string, message: string) => issues.push({ sectionId: section.id, index, reason, message });
  if (sections.length > 200) return [{ sectionId: '', index: 200, reason: 'too_many_sections', message: 'Use at most 200 sections.' }];
  sections.forEach((section, index) => {
    if (!conversationSectionSchema.safeParse(section).success) {
      add(section, index, 'invalid_section', 'Check the section title and membership.');
      return;
    }
    const nestedMembers = new Set<string>();
    for (const block of section.blocks ?? []) {
      if (nestedIds.has(block.id)) add(section, index, 'duplicate_nested_form', 'Each block needs a unique identity.');
      nestedIds.add(block.id);
      const members = block.fieldIds;
      const start = positions.get(members[0]);
      const ordered = start !== undefined && members.every((id, offset) =>
        positions.get(id) === start + offset && section.fieldIds.includes(id) && !nestedMembers.has(id));
      if (!ordered) add(section, index, 'invalid_nested_membership', 'Keep each block together in its section.');
      for (const id of members) nestedMembers.add(id);
      const earlier = fields.slice(0, start ?? 0);
      if (block.showWhen) {
        const issue = questionConditionIssue(block.showWhen, earlier);
        if (issue) add(section, index, 'invalid_condition', issue);
      }
      if (validateFormFields(block.sourceFields).length || validateConversationSections(block.sourceFields, block.sourceSections).length) {
        add(section, index, 'invalid_nested_source', 'Check the source questions.');
      }
      const childFields = block.fieldIds.flatMap(id => {
        const position = positions.get(id);
        return position === undefined ? [] : [fields[position]];
      });
      if (childFields.length !== block.sourceFields.length || childFields.some((field, position) => 'profileBinding' in field || field.type !== block.sourceFields[position]?.type)
        || validateFormFields(childFields).length) {
        add(section, index, 'invalid_nested_fields', 'Check the questions in the nested form.');
      }
      for (const issue of validateConversationSections(childFields, block.sections)) add(section, index, issue.reason, issue.message);
    }
    if (positions.has(section.id)) add(section, index, 'duplicate_section_id', 'Section and question identities must differ.');
    if (section.showWhen !== undefined) {
      const issue = questionConditionIssue(section.showWhen, sectionConditionSources(fields, sections, section.id));
      if (issue) add(section, index, 'invalid_condition', issue);
    }
    if (ids.has(section.id)) add(section, index, 'duplicate_section_id', 'Each section needs a unique identity.');
    ids.add(section.id);
    const positionsInSection = section.fieldIds.map(id => positions.get(id));
    for (const id of section.fieldIds) {
      if (!positions.has(id)) add(section, index, 'unknown_field', 'A section references a question that no longer exists.');
      if (owners.has(id)) add(section, index, 'duplicate_membership', 'A question can belong to only one section.');
      owners.set(id, section.id);
    }
    if (section.fieldIds.length && section.beforeFieldId !== undefined) add(section, index, 'unexpected_empty_anchor', 'Only empty sections use a position anchor.');
    if (section.beforeFieldId !== undefined && !positions.has(section.beforeFieldId)) add(section, index, 'unknown_anchor', 'The empty section position no longer exists.');
    const start = positionsInSection[0] ?? (section.beforeFieldId ? positions.get(section.beforeFieldId) : fields.length) ?? fields.length;
    if (positionsInSection.some((position, offset) => position !== start + offset)) add(section, index, 'noncontiguous_membership', 'Keep the questions in a section together and in conversation order.');
    if (start < previousEnd) add(section, index, 'section_order', 'Keep sections in conversation order.');
    previousEnd = start + section.fieldIds.length;
    if (section.fieldIds.length) ranges.push({ start, end: previousEnd });
  });
  sections.forEach((section, index) => {
    if (section.fieldIds.length) return;
    const anchor = section.beforeFieldId ? positions.get(section.beforeFieldId) : fields.length;
    if (anchor !== undefined && (ranges.some(range => anchor > range.start && anchor < range.end) || fields[anchor]?.informationFieldId)) add(section, index, 'invalid_empty_boundary', 'Place empty sections between complete question groups.');
  });
  for (const field of fields) {
    if (field.informationFieldId && owners.get(field.id) !== owners.get(field.informationFieldId)) {
      const owner = owners.get(field.id) ?? owners.get(field.informationFieldId);
      const index = sections.findIndex(section => section.id === owner);
      if (index >= 0) add(sections[index], index, 'split_information_group', 'Keep Information and its attached question in the same section.');
    }
  }
  return issues;
}

/** Clean stale draft references without reordering or replacing any questions. */
export function normalizeConversationSections(fields: readonly FormField[], sections?: readonly ConversationSection[]): ConversationSection[] {
  if (!sections) return [];
  const positions = new Map(fields.map((field, index) => [field.id, index]));
  const ids = new Set<string>();
  const owner = new Map<string, string>();
  const unique = sections.filter(section => { if (ids.has(section.id)) return false; ids.add(section.id); return true; });
  for (const section of unique) for (const id of section.fieldIds) if (positions.has(id) && !owner.has(id)) owner.set(id, section.id);
  for (const field of fields) {
    if (!field.informationFieldId) continue;
    const sectionId = owner.get(field.informationFieldId) ?? owner.get(field.id);
    if (sectionId) { owner.set(field.id, sectionId); owner.set(field.informationFieldId, sectionId); }
  }
  return unique.map(section => {
    const { beforeFieldId, ...metadata } = section;
    const fieldIds = fields.filter(field => owner.get(field.id) === section.id).map(field => field.id);
    return { ...metadata, fieldIds, ...(!fieldIds.length && beforeFieldId && positions.has(beforeFieldId) ? { beforeFieldId } : {}) };
  }).sort((a, b) => {
    const start = (section: ConversationSection) => positions.get(section.fieldIds[0] ?? section.beforeFieldId ?? '') ?? fields.length;
    return start(a) - start(b) || Number(a.fieldIds.length > 0) - Number(b.fieldIds.length > 0);
  });
}

/** Project every field once; ungrouped fields retain their place between sections. */
export function getConversationSectionBlocks(fields: readonly FormField[], sections?: readonly ConversationSection[]): ConversationSectionBlock[] {
  const normalized = normalizeConversationSections(fields, sections);
  const positions = new Map(fields.map((field, index) => [field.id, index]));
  const owners = new Map(normalized.flatMap(section => section.fieldIds.map(id => [id, section] as const)));
  const empty = new Map<number, ConversationSection[]>();
  for (const section of normalized) if (!section.fieldIds.length) {
    const position = positions.get(section.beforeFieldId ?? '') ?? fields.length;
    empty.set(position, [...(empty.get(position) ?? []), section]);
  }
  const blocks: ConversationSectionBlock[] = [];
  let index = 0;
  while (index <= fields.length) {
    for (const section of empty.get(index) ?? []) blocks.push({ kind: 'section', section, fields: [], startIndex: index });
    if (index === fields.length) break;
    const startIndex = index;
    const section = owners.get(fields[index].id);
    const group: FormField[] = [fields[index++]];
    while (index < fields.length && !empty.has(index) && owners.get(fields[index].id) === section) group.push(fields[index++]);
    blocks.push(section ? { kind: 'section', section, fields: group, startIndex } : { kind: 'ungrouped', fields: group, startIndex });
  }
  return blocks;
}

/** Clone metadata and its references with fresh identities without changing answer keys. */
export function cloneConversationSections(sections: readonly ConversationSection[], sourceFields: readonly FormField[], clonedFields: readonly FormField[]): ConversationSection[] {
  const ids = new Map(sourceFields.map((field, index) => [field.id, clonedFields[index]?.id]));
  const nestedIds = new Map(sections.flatMap(section => (section.blocks ?? []).map(block => [block.id, crypto.randomUUID()] as const)));
  const sectionIds = new Map(sections.map(section => {
    const synthetic = section.blocks?.find(block => section.id === `nested_${block.id}`);
    return [section.id, synthetic ? `nested_${nestedIds.get(synthetic.id)}` : crypto.randomUUID()] as const;
  }));
  const optionIds = new Map<string, string>();
  const matrixRows = new Map<string, string>();
  const matrixColumns = new Map<string, string>();
  for (const [index, source] of sourceFields.entries()) {
    const cloned = clonedFields[index];
    if ('options' in source && cloned && 'options' in cloned) source.options.forEach((option, position) => {
      if (cloned.options[position]) optionIds.set(`${source.id}:${option.id}`, cloned.options[position].id);
    });
    if (source.type === 'matrix' && cloned?.type === 'matrix') for (const axis of ['rows', 'columns'] as const) source.matrix[axis].forEach((item, position) => {
      if (cloned.matrix[axis][position]) (axis === 'rows' ? matrixRows : matrixColumns).set(`${source.id}:${item.id}`, cloned.matrix[axis][position].id);
    });
  }
  /** Copy gate subjects, selected options and structured parts together. */
  const cloneCondition = (condition: QuestionCondition): QuestionCondition => ({ ...condition, rules: condition.rules.map(rule => ({ ...rule,
    fieldId: ids.get(rule.fieldId) ?? sectionIds.get(rule.fieldId) ?? rule.fieldId,
    ...(typeof rule.value === 'string' ? { value: matrixColumns.get(`${rule.fieldId}:${rule.value}`) ?? optionIds.get(`${rule.fieldId}:${rule.value}`) ?? rule.value } : {}),
    ...(rule.part ? { part: matrixRows.get(`${rule.fieldId}:${rule.part}`) ?? optionIds.get(`${rule.fieldId}:${rule.part}`) ?? rule.part } : {}),
  })) });
  return sections.map(section => ({ ...structuredClone(section), id: sectionIds.get(section.id)!,
    fieldIds: section.fieldIds.flatMap(id => ids.get(id) ? [ids.get(id)!] : []),
    ...(section.beforeFieldId ? { beforeFieldId: ids.get(section.beforeFieldId) } : {}),
    ...(section.showWhen ? { showWhen: cloneCondition(section.showWhen) } : {}),
    ...(section.blocks ? { blocks: section.blocks.map(block => ({
      ...structuredClone(block), id: nestedIds.get(block.id)!,
      ...(block.showWhen ? { showWhen: cloneCondition(block.showWhen) } : {}),
      fieldIds: block.fieldIds.map(id => ids.get(id) ?? id),
      sections: cloneConversationSections(block.sections, sourceFields, clonedFields),
    })) } : {}),
  }));
}

/** Only facts preceding the entire section can gate its members. */
export function sectionConditionSources(fields: readonly FormField[], sections: readonly ConversationSection[], sectionId: string): QuestionConditionSource[] {
  const index = sections.findIndex(section => section.id === sectionId);
  const section = sections[index];
  if (!section) return [];
  const start = fields.findIndex(field => field.id === (section.fieldIds[0] ?? section.beforeFieldId));
  const boundary = start < 0 ? fields.length : start;
  const earlier = sections.slice(0, index).filter(candidate => candidate.fieldIds.every(id => fields.findIndex(field => field.id === id) < boundary));
  return [...fields.slice(0, boundary), ...earlier.map(candidate => ({ id: candidate.id, name: candidate.id, label: candidate.title, type: 'section' }))];
}

/** Section presence describes its selected path, including an explicit skipped branch. */
export function sectionConditionFacts(sections: readonly ConversationSection[], answers: Readonly<Record<string, ConditionAnswer>>): Record<string, ConditionAnswer> {
  const facts: Record<string, ConditionAnswer> = { ...answers };
  for (const section of sections) {
    const eligible = evaluateQuestionCondition(section.showWhen, facts);
    facts[section.id] = { status: eligible === 'pending' ? 'pending' : eligible ? 'answered' : 'unanswered' };
  }
  return facts;
}

/** Resolve the section gate independently of a member's own all/any rule group. */
export function fieldSectionEligibility(fieldId: string, sections: readonly ConversationSection[], answers: Readonly<Record<string, ConditionAnswer>>): boolean | 'pending' {
  const section = sections.find(candidate => candidate.fieldIds.includes(fieldId));
  if (!section) return true;
  const facts = sectionConditionFacts(sections, answers);
  const status = facts[section.id]?.status;
  const gates: (boolean | 'pending')[] = [status === 'pending' ? 'pending' : status === 'answered'];
  const block = section.blocks?.find(child => child.fieldIds.includes(fieldId));
  if (block) {
    gates.push(evaluateQuestionCondition(block.showWhen, answers));
    gates.push(fieldSectionEligibility(fieldId, block.sections, answers));
  }
  return gates.includes(false) ? false : gates.includes('pending') ? 'pending' : true;
}
